import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { countBy, size } from 'lodash'

import { Button } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import { formatNumber } from '@/utils'

import { FileValidationStatus } from '../../../schema'
import { useFileUploadFiles, useFileUploadStore } from '../store'
import { UploadPhase } from '../types'

export const FileStatusTabs: React.FC<{
  className?: string
}> = ({ className = '' }) => {
  const { t } = useTranslation()
  const files = useFileUploadFiles()
  const { filters, setFilter, uploadPhase } = useFileUploadStore()

  const statusCounts = React.useMemo(() => {
    const counts = countBy(files, 'validationStatus')

    return {
      all: size(files),
      // Step 1: Validation Phase
      [FileValidationStatus.VALIDATING]:
        counts[FileValidationStatus.VALIDATING] || 0,
      [FileValidationStatus.READY_TO_UPLOAD]:
        counts[FileValidationStatus.READY_TO_UPLOAD] || 0,
      [FileValidationStatus.FAILED]: counts[FileValidationStatus.FAILED] || 0,
      [FileValidationStatus.CANCELLED]:
        counts[FileValidationStatus.CANCELLED] || 0,
      [FileValidationStatus.ACTION_REQUIRED]:
        // Check if any file isActionRequired is true
        countBy(files, 'isActionRequired')['true'] ||
        counts[FileValidationStatus.ACTION_REQUIRED] ||
        0,
      // Step 2: Upload Phase
      [FileValidationStatus.UPLOADING]:
        counts[FileValidationStatus.UPLOADING] || 0,
      [FileValidationStatus.UPLOADED]:
        counts[FileValidationStatus.UPLOADED] || 0,
    }
  }, [files])

  const validationPhaseTabs = useMemo(
    () => [
      {
        id: 'all' as const,
        label: t('fileUpload.tabs.all'),
        count: statusCounts.all,
      },
      {
        id: FileValidationStatus.VALIDATING,
        label: t('fileUpload.tabs.validating'),
        count: statusCounts[FileValidationStatus.VALIDATING],
      },
      {
        id: FileValidationStatus.READY_TO_UPLOAD,
        label: t('fileUpload.tabs.readyToUpload'),
        count: statusCounts[FileValidationStatus.READY_TO_UPLOAD],
      },
      {
        id: FileValidationStatus.FAILED,
        label: t('fileUpload.tabs.failed'),
        count: statusCounts[FileValidationStatus.FAILED],
      },
      {
        id: FileValidationStatus.CANCELLED,
        label: t('fileUpload.tabs.cancelled'),
        count: statusCounts[FileValidationStatus.CANCELLED],
      },
      {
        id: FileValidationStatus.ACTION_REQUIRED,
        label: t('fileUpload.tabs.actionRequired'),
        count: statusCounts[FileValidationStatus.ACTION_REQUIRED],
      },
    ],
    [statusCounts, t]
  )

  const uploadPhaseTabs = useMemo(
    () => [
      {
        id: 'all' as const,
        label: t('fileUpload.tabs.all'),
        count: statusCounts.all,
      },
      {
        id: FileValidationStatus.UPLOADING,
        label: t('fileUpload.tabs.uploading'),
        count: statusCounts[FileValidationStatus.UPLOADING],
      },
      {
        id: FileValidationStatus.UPLOADED,
        label: t('fileUpload.tabs.uploaded'),
        count: statusCounts[FileValidationStatus.UPLOADED],
      },
    ],
    [statusCounts, t]
  )

  const tabs = useMemo(
    () =>
      uploadPhase === UploadPhase.UPLOAD
        ? uploadPhaseTabs
        : validationPhaseTabs,
    [uploadPhase, uploadPhaseTabs, validationPhaseTabs]
  )

  const handleTabChange = (tabId: 'all' | FileValidationStatus) => {
    setFilter({ status: tabId })
  }

  return (
    <div className={className}>
      <ButtonGroup orientation="horizontal" className="!gap-0 shadow-sm">
        {tabs.map((tab) => {
          const isActive = filters.status === tab.id
          return (
            <Button
              key={tab.id}
              data-slot="button-group"
              onClick={() => handleTabChange(tab.id)}
              variant={isActive ? 'solid' : 'outlined'}
              color={isActive ? 'primary' : 'default'}
              className="whitespace-nowrap"
            >
              {tab.label} ({formatNumber(tab.count)})
            </Button>
          )
        })}
      </ButtonGroup>
    </div>
  )
}
